import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Zap, Users, Target, CheckCircle, Search, Settings, Shield, Globe, TrendingUp, Briefcase } from 'lucide-react';
import Home from '../Home';
import HeroBgImg from '../../img/reputation-management-dashboard.png'
import HandShake from '../../img/handshake.jpg'

const foundationStones = [
  { title: "Online Monitoring and Analysis", icon: Search, text: "Continuous tracking and analysis of all online mentions, reviews, and social media conversations related to your brand." },
  { title: "Optimize Profiles", icon: Settings, text: "Ensuring all your online profiles (social, directories, review sites) are complete, consistent, and optimized for search." },
  { title: "Negative Content Suppression", icon: Shield, text: "Implementing strategic SEO and content creation to push negative search results lower and replace them with positive assets." },
  { title: "Positive Content Creation", icon: CheckCircle, text: "Developing high-quality, engaging content to proactively shape a favorable perception of your brand across all digital channels." },
  { title: "Strategic Brand Management", icon: Globe, text: "Defining and communicating a cohesive brand message that resonates with your target audience and strengthens market position." },
  { title: "Crisis Management", icon: Zap, text: "Rapid response strategies to mitigate damage during a PR crisis, ensuring controlled communication and speedy resolution." }
];

const processSteps = [
  { 
    title: "Assessment & Planning", 
    description: "We conduct an in-depth audit of your current online presence, identifying weaknesses, opportunities, and establishing key performance indicators (KPIs)." 
  },
  { 
    title: "Strategy & Implementation", 
    description: "Based on the audit, we craft a bespoke ORM strategy, focusing on content creation, SEO optimization, and profile cleanup. Implementation begins immediately." 
  },
  { 
    title: "Content Creation", 
    description: "Dedicated resources are allocated to generate positive content that organically pushes down undesirable search results." 
  },
  { 
    title: "Performance Tracking & Reporting", 
    description: "We continuously monitor and report on the performance of the implemented strategies, providing transparent, detailed insights into reputation changes." 
  }
];

const usps = [
  { title: "Holistic Approach", icon: Users, text: "We consider all digital touchpoints—from search to social—ensuring a unified strategy." },
  { title: "Data-driven Strategy", icon: Target, text: "Our decisions are based on continuous data analysis and real-time monitoring insights." },
  { title: "Collaboration for Success", icon: Briefcase, text: "We work as an extension of your team, ensuring transparency and alignment with your business goals." },
  { title: "Customized Recovery Plans", icon: TrendingUp, text: "Every plan is unique, designed specifically to address your industry's specific challenges and threats." }
];

const faqs = [
  { q: "What is Online Reputation Management (ORM)?", a: "ORM involves monitoring, influencing, and controlling an individual's or business's reputation on the internet. This includes managing search results, social media mentions, and online reviews." },
  { q: "How long does it take to see results?", a: "Results vary based on the severity of the negative content and the competitive landscape. Generally, clients see noticeable improvements within 3-6 months, with continuous long-term management being key." },
  { q: "Is ORM the same as PR?", a: "No. While related, PR focuses on outward-facing communication and media relations. ORM specifically addresses the digital presence and search engine visibility of that reputation." },
  { q: "What types of content can you suppress?", a: "We focus on suppressing negative articles, forum posts, negative reviews, and undesirable search suggestions through strategic content creation and SEO techniques." }
];

const SocialMediaReputationManagement = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [openFAQ, setOpenFAQ] = useState(null);

  const PrimaryButton = ({ children, className = '' }) => (
    <button 
      className={`bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-8 rounded-lg text-lg font-semibold cursor-pointer transition duration-200 shadow-xl shadow-red-500/50 hover:shadow-red-500/80 hover:scale-[1.02] w-full sm:w-auto ${className}`}
    >
      {children}
    </button>
  );

  const SectionContainer = ({ children, className = '' }) => (
    <section className={`py-12 lg:py-20 px-4 sm:px-6 lg:px-8 text-center ${className}`}>
      <div className="max-w-7xl mx-auto">
        {children}
      </div>
    </section>
  );

  return (
    <Home>
      <div className="min-h-screen w-full overflow-x-hidden font-inter text-gray-800">
        
        {/* 1. Hero Section */}
        <header 
          className="relative bg-black bg-cover bg-center flex items-center justify-between py-16 lg:py-32 px-4 sm:px-6 lg:px-8"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.8)), url(${HeroBgImg})`,
          }}
        >
          <div className="max-w-7xl mx-auto w-full flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left lg:max-w-xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
                Like A Superhero,{' '}
                <span className="text-red-400 block">Sculpt Your Online Reputation</span>
              </h1>
              <p className="text-gray-300 text-lg sm:text-xl mb-8 max-w-2xl">
                Mold your brand narrative. Protect your image. Partner with SRK Software for Reputation Management.
              </p>
              <PrimaryButton className="text-base py-3 px-8 mx-auto lg:mx-0">Start Protecting Your Brand</PrimaryButton>
            </div>
            
            <div 
              className="w-48 h-48 sm:w-52 sm:h-52 lg:w-64 lg:h-64 rounded-full bg-gray-800 flex items-center justify-center bg-cover bg-center flex-shrink-0"
              style={{ backgroundImage: `url('https://placehold.co/250x250/ee5253/fff?text=OR+Superhero+Image')` }}
            >
            </div>
          </div>
        </header>

        {/* 2. Why Prioritize Section */}
        <SectionContainer className="bg-blue-50">
          <h2 className="text-xl sm:text-2xl font-bold text-blue-900 inline-block border-b-4 border-blue-900 pb-2 mb-8 max-w-4xl">
            Why Should You Prioritize Online Reputation Management Services for Your Business?
          </h2>
          <div className="flex flex-col lg:flex-row items-center gap-8 max-w-6xl mx-auto">
            <div className="w-full lg:w-1/2 order-2 lg:order-1">
              <p className="mb-4 text-gray-700 text-left">
                In today's digital economy, your brand is what Google says it is. Negative reviews, unmanaged social media comments, or old, misleading articles can severely damage consumer trust and sales conversions. A proactive ORM strategy ensures that the digital narrative around your company is accurate, positive, and aligned with your core values.
              </p>
              <p className="text-gray-700 text-left">
                SRK Software helps you take control, ensuring potential customers and partners see the best version of your brand first. Don't let a single bad review define your business.
              </p>
            </div>
            <div 
              className="w-full lg:w-1/2 h-48 sm:h-64 bg-blue-200 rounded-lg bg-cover bg-center order-1 lg:order-2"
              style={{ backgroundImage: `url('https://placehold.co/600x400/c0d9ff/0d47a1?text=Illustration+Image')` }}
            ></div>
          </div>
        </SectionContainer>
        
        {/* 3. Foundation Stones Section */}
        <SectionContainer className="bg-gray-900 text-white">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-8">
            The Foundation Stones of Our Online Reputation Management in Digital Marketing.
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-8">
            {foundationStones.map((stone, index) => (
              <div 
                key={index} 
                className="bg-gray-800/85 border border-gray-700 p-4 sm:p-6 rounded-xl text-center shadow-lg transition duration-300 min-h-[200px] sm:min-h-[240px] flex flex-col justify-center hover:transform hover:-translate-y-1 hover:border-red-500"
              >
                <div className="text-red-500 mb-3 text-3xl mx-auto">
                  {React.createElement(stone.icon, { size: 32 })}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-red-400 mb-2">{stone.title}</h3>
                <p className="text-gray-400 text-sm sm:text-base">{stone.text}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-gray-700/90 p-4 sm:p-6 rounded-xl mt-6 sm:mt-8">
            <p className="text-base text-gray-300 text-center sm:text-left">
              These pillars form a comprehensive shield and strategy, designed to build, maintain, and defend your brand's reputation across all digital platforms. We build positive assets, minimize negative visibility, and establish robust monitoring systems.
            </p>
          </div>
        </SectionContainer>

        {/* 4. Process Section */}
        <SectionContainer className="bg-white">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-8">Our Process of Reputation Management</h2>
          
          <div className="flex flex-col items-center">
            {/* Step Group / Tabs */}
            <div className="w-full max-w-4xl flex flex-col sm:flex-row gap-2 sm:gap-4 mb-8">
              {processSteps.map((step, index) => (
                <div 
                  key={index}
                  className={`cursor-pointer py-3 sm:py-4 px-4 sm:px-5 rounded-lg font-semibold text-center transition duration-300 shadow-md flex-1 ${
                    activeStep === index 
                      ? 'bg-red-500 text-white shadow-red-400/50' 
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                  onClick={() => setActiveStep(index)}
                >
                  <span className="text-sm sm:text-base">{step.title}</span>
                </div>
              ))}
            </div>
            
            {/* Description Box */}
            <div className="w-full max-w-4xl bg-gray-50 border border-red-500 rounded-xl p-6 sm:p-8 mt-4 text-left shadow-lg">
              <h4 className="text-xl sm:text-2xl text-red-600 font-bold mb-3">{processSteps[activeStep].title}</h4>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                {processSteps[activeStep].description}
              </p>
              <p className="mt-4 font-bold text-red-500 text-sm sm:text-base">
                "{processSteps[activeStep].title === 'Assessment & Planning' ? 
                'This is where we define the target and map the entire strategy.' :
                'Detailed explanation of this step, providing transparency in the process.'}"
              </p>
            </div>
          </div>
        </SectionContainer>

        {/* 5. Why Partner Section */}
        <SectionContainer className="bg-gray-50">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-8">Why SRK Software is Your Ideal Partner for This Journey</h2>
          
          <div className="flex flex-col lg:flex-row items-center gap-8 max-w-6xl mx-auto my-8">
            <div 
              className="w-full lg:w-1/2 h-60 sm:h-64 bg-gray-300 rounded-lg bg-cover bg-center"
              style={{ backgroundImage: `url(${HandShake})` }}
            ></div>
            
            <div className="w-full lg:w-1/2 text-left">
              <p className="mb-4 text-gray-600 text-sm sm:text-base">
                When it comes to safeguarding your digital future, you need more than just a vendor—you need a vested partner. SRK Software brings years of expertise, specialized tools, and a dedicated team focused solely on mitigating risk and elevating your brand.
              </p>
              <p className="text-gray-600 text-sm sm:text-base">
                We are committed to transparent reporting, ethical practices, and achieving quantifiable results. We understand that your reputation is your most valuable asset, and we treat it that way.
              </p>
            </div>
          </div>
          
          {/* USP Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto mt-8">
            {usps.map((usp, index) => (
              <div 
                key={index} 
                className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 text-center shadow-lg transition duration-200 hover:shadow-xl min-h-[160px] sm:min-h-[180px] flex flex-col items-center justify-center"
              >
                <div className="text-red-500 mb-3">
                  {React.createElement(usp.icon, { size: 32 })}
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2">{usp.title}</h4>
                <p className="text-xs sm:text-sm text-gray-600">{usp.text}</p>
              </div>
            ))}
          </div>
        </SectionContainer>

        {/* 6. FAQ Section */}
        <SectionContainer className="bg-white">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-8">Frequently Asked Questions</h2>
          
          <div className="max-w-4xl mx-auto mt-8 text-left">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 py-4">
                <button 
                  className="w-full flex justify-between items-center text-base sm:text-lg font-semibold text-gray-800 cursor-pointer p-2 hover:text-red-500 transition duration-150 text-left"
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                >
                  <span className="pr-4">{faq.q}</span>
                  <span className="text-red-500 flex-shrink-0">
                    {openFAQ === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </span>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${openFAQ === index ? 'max-h-96 py-3' : 'max-h-0'}`}
                >
                  <p className="text-gray-600 pl-4 border-l-2 border-red-500 ml-1 py-1 text-sm sm:text-base">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </SectionContainer>

       
      </div>
    </Home>
  );
};

export default SocialMediaReputationManagement;